import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  contactLinks,
  deleteMyCompanyContact,
  getMemberCertificate,
  getMemberContact,
  getMemberLocations,
  saveMemberContact,
  saveMemberLocations,
} from 'services/members';

export const useMutationSaveContact = () => {
  const mutation = useMutation({ mutationFn: saveMemberContact });

  return mutation;
};

export const useMutationSaveLocation = () => {
  const mutation = useMutation({ mutationFn: saveMemberLocations });

  return mutation;
};

export const useMutationContactLinks = () => {
  const mutation = useMutation({ mutationFn: contactLinks });

  return mutation;
};

export const useMutationDeleteContact = () => {
  const mutation = useMutation({ mutationFn: deleteMyCompanyContact });

  return mutation;
};

export const getLocationListKey = {
  getLocation: (memberId) => ['company', 'location', parseInt(memberId)],
};

export const getContactListKey = {
  getContact: (memberId) => ['company', 'contact', parseInt(memberId)],
};

export const getCertificateListKey = {
  getCertificate: (memberId) => ['company', 'certificate', parseInt(memberId)],
};

export const useGetLocationForMember = ({ memberId, ...otherOptions }) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: getLocationListKey.getLocation(memberId),
    queryFn: async () =>
      await getMemberLocations({
        memberId: memberId,
        getAll: true,
      }),
    enabled: Boolean(memberId),
    retry: 1,
    ...otherOptions,
  });

  const handleRefetchLocations = () => {
    queryClient.invalidateQueries({
      queryKey: getLocationListKey.getLocation(memberId),
    });
  };

  return {
    locationList: queries?.data?.gridData || [],
    handleRefetchLocations,
    ...queries,
  };
};

export const useGetContactForMember = ({
  memberId,
  productId,
  ...otherOptions
}) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: getContactListKey.getContact(memberId),
    queryFn: async () =>
      await getMemberContact({
        memberId: memberId,
        getAll: true,
        productId: productId,
      }),
    enabled: Boolean(memberId),
    retry: 1,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,

    ...otherOptions,
  });

  const handleRefetchContact = () => {
    queryClient.invalidateQueries({
      queryKey: getContactListKey.getContact(memberId),
    });
  };

  return {
    contactList: queries?.data?.gridData || [],
    handleRefetchContact,
    ...queries,
  };
};

export const useGetCertificateForMember = ({ memberId, ...otherOptions }) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: getCertificateListKey.getCertificate(memberId),
    queryFn: async () =>
      await getMemberCertificate({
        memberId: memberId,
        getAll: true,
        includeArchived: true, //get all
        //includeArchived: otherOptions?.includeArchived ?? false
      }),
    enabled: Boolean(memberId),
    retry: 1,
    ...otherOptions,
  });

  const handleRefetchCertificate = () => {
    queryClient.invalidateQueries({
      queryKey: getCertificateListKey.getCertificate(memberId),
    });
  };

  return {
    certificateList: queries?.data?.gridData || [],
    handleRefetchCertificate,
    ...queries,
  };
};
